/**
 * Kaplan-Meier 생존곡선 — 계단 + at-risk 표 (DESIGN §3.3.1.1 · §8.6.3).
 *
 * **왜 계단인가.** KM 추정값은 사건이 관측된 시각에만 바뀐다. 두 사건 사이를
 * 직선으로 이으면 *관측하지 않은 시각의 값을 발명하는 것* 이다. 그 구간에서
 * 우리가 아는 것은 "아직 아무도 복구되지 않았다" 뿐이고, 그건 수평선이다.
 *
 * **왜 곡선을 다 그리지 않는가.** 축에 따라 최대 42개가 온다. 42개를 다른 색으로
 * 그리면 색이 8색을 넘어(§8.6.2 금지) 아무것도 구분되지 않는다. 그래서 **두 개만
 * 비교 계열색으로 강조**하고 나머지 판정된 곡선은 회색 배경으로 문맥만 준다.
 * 배경으로 민 개수와 아예 그리지 않은 개수는 화면에 인쇄한다.
 *
 * **왜 판정 보류 곡선은 그리지 않는가.** 사건 3건으로 그린 KM 은 계단 하나가
 * 33%p 다 — 소수점을 붙여 제시할 정밀도가 아니다. 그리는 대신 목록으로 남기고
 * 사유를 적는다 ("이 코호트가 나쁘다"와 "우리가 아직 모른다"는 다른 진술이다).
 */

import type { EChartsOption, LineSeriesOption, ScatterSeriesOption } from 'echarts'
import { useMemo, useState } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { Headline } from '@/components/WidgetShell'
import { axisStyle, CHART_H, niceBand } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { DASH, int, pct, pctAxis } from '@/lib/fmt'
import { useEntityLabel } from '@/lib/labels'
import type { Envelope } from '@/lib/types'
import type { CohortAxis, KMCurve, KMStep, SurvivalData } from '../CohortPage'

interface Props {
  env?: Envelope<SurvivalData>
  axis: { key: CohortAxis; label: string }
  isLoading: boolean
  error: Error | null
  onRetry: () => void
}

/** 그룹 값이 결측인 코호트의 내부 식별자 — 지우지 않고 하나의 그룹으로 남긴다 */
const MISSING = '__missing__'
/** 두 번째 곡선 없음 */
const NONE = '__none__'

/** 표 뷰에 싣는 곡선당 계단 수 상한. 넘으면 개수를 밝히고 자른다 */
const TABLE_STEPS = 500

/**
 * 세로축은 항상 0~100% 다 — 확률은 절대 척도라 축을 확대하지 않는다.
 * 상수이므로 모듈 밖에서 한 번만 만든다 (렌더마다 새 객체를 만들면 차트가
 * 매 렌더 다시 초기화된다).
 */
const SURVIVAL_BAND = niceBand(0, 1)

/**
 * 시간축 눈금 후보 (분).
 *
 * `niceBand` 는 비율 축용이라 500·2,500 같은 간격을 고른다 — 시간축에서는
 * 사람이 읽지 못하는 값이다. 시·일 경계에 맞는 값만 후보로 두고 그중에서 고른다
 * (§8.6.3 "축은 계산한다"를 시간축에 적용한 것이다).
 */
const TIME_STEPS = [5, 10, 15, 30, 60, 120, 180, 360, 720, 1440, 2880, 10080, 43200]

function timeBand(maxMin: number, target = 5) {
  const raw = Math.max(maxMin, 1) / target
  const interval = TIME_STEPS.find((s) => s >= raw) ?? TIME_STEPS[TIME_STEPS.length - 1] ?? 1440
  return { min: 0, max: Math.ceil(Math.max(maxMin, 1) / interval) * interval, interval }
}

/**
 * 시간축 라벨 서식 — **한 축의 단위는 하나다** (`fmt.pctAxis` 와 같은 원칙).
 * 눈금마다 분·시간·일이 섞이면 읽는 사람이 없는 규칙을 찾게 된다.
 */
function minuteAxis(interval: number): (v: number) => string {
  const unit = interval >= 1440 ? 1440 : interval >= 60 ? 60 : 1
  const suffix = unit === 1440 ? '일' : unit === 60 ? '시간' : '분'
  const digits = Number.isInteger(interval / unit) ? 0 : 1
  return (v: number) => `${(v / unit).toFixed(digits)}${suffix}`
}

/** 하나의 값을 사람이 읽는 길이로. 축이 아니라 문장에 쓴다 (중위 생존·헤드라인) */
function humanMinutes(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return DASH
  if (v < 60) return `${v.toFixed(0)}분`
  if (v < 1440) return `${Math.floor(v / 60)}시간 ${Math.round(v % 60)}분`
  return `${Math.floor(v / 1440)}일 ${Math.round((v % 1440) / 60)}시간`
}

/**
 * **계단 표의 경과 시각은 분 아래를 버리지 않는다.**
 *
 * KM 계단은 1초 간격으로도 갈라진다 (실측 t = 30.000 · 30.017 · 30.033 …).
 * 문장용 `humanMinutes` 로 찍으면 서로 다른 아홉 계단이 전부 **"30분"** 이 되고,
 * 생존확률만 96.9% → 59.9% 로 떨어지는 표가 된다 — 같은 시각에 값이 아홉 개
 * 있는 것처럼 보인다. 표는 값을 *정확히* 읽는 경로인데 거기서 시각을 잃으면
 * 차트보다 못하다. 그래서 표에서만 소수 둘째 자리(≈0.6초)까지 남긴다.
 */
function stepTime(v: number): string {
  if (Number.isNaN(v)) return DASH
  if (v < 60) return `${v.toFixed(2)}분`
  return `${Math.floor(v / 60)}시간 ${(v % 60).toFixed(2)}분`
}

function idOf(c: KMCurve): string {
  return c.group ?? MISSING
}

/**
 * 시각 t 의 계단 — **t 이하의 마지막 계단**.
 *
 * KM 은 계단함수라 두 사건 사이의 값은 앞 계단이 그대로 이어진다. 곡선의 마지막
 * 계단보다 뒤는 관측이 끝난 구간이므로 값을 만들지 않고 `null` 로 돌려준다 —
 * 마지막 값을 오른쪽 끝까지 늘려 적으면 없는 관측을 있는 것처럼 말하게 된다.
 */
function stepAt(c: KMCurve, t: number): KMStep | null {
  const last = c.steps[c.steps.length - 1]
  if (!last || t > last.t) return null
  let hit: KMStep | null = null
  for (const s of c.steps) {
    if (s.t > t) break
    hit = s
  }
  return hit
}

/**
 * 시각 T 에 남아 있던 대상 수.
 *
 * `at_risk` 는 그 계단 **직전** 의 잔존 수이므로, T 이상인 첫 계단의 값이 곧
 * T 시점의 잔존 수다. 마지막 계단보다 뒤면 그 계단에서 빠진 만큼을 뺀 나머지다.
 * 계단이 추려진 곡선에서는 중간 계단이 없어 **과소** 로 나올 수 있다 — 그 사실은
 * 각주에 적는다 (없는 정밀도를 지어내지 않는다).
 */
function atRiskAt(c: KMCurve, t: number): number {
  const hit = c.steps.find((s) => s.t >= t)
  if (hit) return hit.at_risk
  const last = c.steps[c.steps.length - 1]
  if (!last) return 0
  return Math.max(0, last.at_risk - last.events - last.censored)
}

export function SurvivalCurves({ env, axis, isLoading, error, onRetry }: Props) {
  const labelOf = useEntityLabel()
  const [pickA, setPickA] = useState<string | null>(null)
  const [pickB, setPickB] = useState<string | null>(null)

  const data = env?.data
  const curves = useMemo(() => data?.curves ?? [], [data])

  /**
   * 판정된 곡선. **사건 수 내림차순** — 근거가 두꺼운 곡선이 위에 온다.
   *
   * 다만 결측 코호트('미상')는 맨 뒤로 보낸다. 실측에서 이 코호트가 사건 수 1위라
   * 그대로 두면 화면을 처음 열었을 때 강조되는 곡선이 '미상'이 된다 — 결측은
   * **지우지도 앞세우지도 않는다** (백엔드 `_sorted_groups` 와 같은 규칙).
   */
  const estimated = useMemo(
    () =>
      curves
        .filter((c) => c.verdict === 'estimated')
        .sort((a, b) => {
          const am = a.group == null ? 1 : 0
          const bm = b.group == null ? 1 : 0
          return am !== bm ? am - bm : b.n_events - a.n_events
        }),
    [curves],
  )
  const withheld = useMemo(
    () =>
      curves
        .filter((c) => c.verdict !== 'estimated')
        .sort((a, b) => b.n_subjects - a.n_subjects),
    [curves],
  )

  const nameOf = (c: KMCurve) =>
    c.group == null ? '미상 (값 없음)' : labelOf(axis.key, c.group)

  // 축·모드가 바뀌면 예전 선택은 존재하지 않는다. 그때만 자동 선택으로 돌아간다
  const has = (id: string | null) => id != null && estimated.some((c) => idOf(c) === id)
  const idA = has(pickA) ? (pickA as string) : (estimated[0] ? idOf(estimated[0]) : '')
  const rawB = pickB === NONE ? NONE : has(pickB) ? (pickB as string) : estimated[1] ? idOf(estimated[1]) : NONE
  const idB = rawB === idA ? NONE : rawB

  const curveA = estimated.find((c) => idOf(c) === idA)
  const curveB = idB === NONE ? undefined : estimated.find((c) => idOf(c) === idB)
  const picked = useMemo(
    () => [curveA, curveB].filter((c): c is KMCurve => c != null),
    [curveA, curveB],
  )
  // 렌더마다 새 배열을 만들면 option 이 매번 새로 계산되고 차트가 통째로 다시
  // 초기화된다 (EChart 는 option 이 바뀌면 인스턴스를 버리고 새로 만든다)
  const background = useMemo(
    () => estimated.filter((c) => c !== curveA && c !== curveB),
    [estimated, curveA, curveB],
  )

  /** 가로축 범위는 **그린 곡선 전체** 기준이다 — 배경 곡선이 잘리면 안 된다 */
  const band = useMemo(() => {
    const tMax = Math.max(
      ...estimated.map((c) => c.steps[c.steps.length - 1]?.t ?? 0),
      1,
    )
    return timeBand(tMax)
  }, [estimated])

  const ticks = useMemo(() => {
    const out: number[] = []
    for (let t = band.min; t <= band.max + 1e-9; t += band.interval) out.push(Number(t.toFixed(6)))
    return out
  }, [band])

  const fmtTick = useMemo(() => minuteAxis(band.interval), [band.interval])

  const option = useMemo<EChartsOption>(() => {
    const ink2 = cssVar('--c-ink-2', '#444446')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const na = cssVar('--c-na', '#6f6f75')
    // 상태색이 아니라 **비교 계열색**이다. 두 곡선은 좋고 나쁨이 아니라 서로 다른
    // 그룹이므로 UP/DOWN 을 쓰면 "이 제조사가 나쁘다"로 읽힌다 (§8.6.2)
    const series = [cssVar('--c-cat-1', '#0066cc'), cssVar('--c-cat-2', '#a35200')]

    const bg: LineSeriesOption[] = background.map((c) => ({
      type: 'line',
      name: nameOf(c),
      step: 'end',
      symbol: 'none',
      silent: true,
      lineStyle: { color: na, width: 1, opacity: 0.3 },
      z: 1,
      data: c.steps.map((s) => [s.t, s.survival, s.at_risk]),
    }))

    const front: LineSeriesOption[] = picked.map((c, i) => ({
      type: 'line',
      name: nameOf(c),
      step: 'end',
      symbol: 'none',
      lineStyle: { color: series[i] ?? series[0], width: 2 },
      z: 4 - i,
      data: c.steps.map((s) => [s.t, s.survival, s.at_risk]),
    }))

    // 중도절단 표시 — KM 의 관례인 '+' 다. 이 페이지의 주제가 절단이므로
    // "언제 관측을 잃었는가"를 곡선 위에서 직접 보여준다
    const ticksSeries: ScatterSeriesOption[] = picked.map((c, i) => ({
      type: 'scatter',
      name: `${nameOf(c)} 절단`,
      symbol: 'path://M-1,-5 h2 v4 h4 v2 h-4 v4 h-2 v-4 h-4 v-2 h4 z',
      symbolSize: 8,
      itemStyle: { color: series[i] ?? series[0] },
      silent: true,
      z: 5,
      data: c.steps.filter((s) => s.censored > 0).map((s) => [s.t, s.survival]),
    }))

    const guide: LineSeriesOption = {
      type: 'line',
      name: '중위 판독선',
      data: [],
      silent: true,
      markLine: {
        silent: true,
        symbol: 'none',
        lineStyle: { color: ink2, type: 'solid', width: 1 },
        label: { formatter: '생존 50%', fontSize: 10, color: ink2, position: 'insideEndTop' },
        data: [{ yAxis: 0.5 }],
      },
    }

    return {
      grid: { left: 56, right: 20, top: 18, bottom: 34 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line' },
        /**
         * **값은 params 가 아니라 계단에서 직접 읽는다.**
         *
         * 곡선마다 사건 시각이 달라서, 값 축의 axisPointer 는 포인터에 가장 가까운
         * *한* 계열의 점에만 붙는다. 그래서 params 에는 대개 배경 곡선 하나만 들어오고,
         * 강조 곡선만 골라 쓰면 걸러진 뒤 빈 문자열이 되어 **툴팁이 아예 안 뜬다**
         * (t=0 처럼 모든 곡선이 점을 공유하는 자리에서만 떴다).
         *
         * KM 은 계단함수라 임의의 시각 값이 정의되어 있다 — 포인터가 가리키는 시각을
         * 받아 강조 곡선의 계단에서 직접 읽으면 어느 자리에서나 같은 규칙으로 나온다.
         */
        formatter: (params) => {
          const list = (Array.isArray(params) ? params : [params]) as { axisValue?: unknown }[]
          const t = list.find((p) => typeof p.axisValue === 'number')?.axisValue
          if (typeof t !== 'number' || picked.length === 0) return ''
          const lines = [`<b>${stepTime(t)} 경과</b>`]
          for (const c of picked) {
            const s = stepAt(c, t)
            lines.push(
              s
                ? `${nameOf(c)} — 생존 ${pct(s.survival, 1)} · 잔존 ${int(s.at_risk)}건`
                : `${nameOf(c)} — 이 시각에는 관측이 남아 있지 않습니다`,
            )
          }
          return lines.join('<br/>')
        },
      },
      xAxis: {
        type: 'value',
        min: band.min,
        max: band.max,
        interval: band.interval,
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: fmtTick },
      },
      yAxis: {
        type: 'value',
        min: SURVIVAL_BAND.min,
        max: SURVIVAL_BAND.max,
        interval: SURVIVAL_BAND.interval,
        ...axisStyle(ink3, hairline),
        axisLabel: {
          fontSize: 10,
          color: ink3,
          formatter: pctAxis(SURVIVAL_BAND.interval * 5),
        },
      },
      series: [...bg, ...front, ...ticksSeries, guide],
    }
    // nameOf 는 labelOf 에만 의존하므로 의존성으로 labelOf 를 둔다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [background, picked, band, fmtTick, labelOf, axis.key])

  const medianOf = (c: KMCurve) =>
    c.median_reached && c.median_survival != null
      ? humanMinutes(c.median_survival)
      : '관측 기간 내에 절반이 복구되지 않았습니다'

  const thinned = estimated.filter((c) => c.steps_total > c.steps.length)
  const notFitted = data != null && data.curve_fitted === false
  const minEvents = data?.min_events ?? 10
  // 응답이 실제로 왔을 때만 "없다"고 말한다 — **아직 못 받은 것과 없는 것은 다르다**
  const isEmpty = !isLoading && !error && data != null && curves.length === 0 && !notFitted

  /** 표 뷰 — 강조한 곡선의 계단 값. 툴팁 없이 정확히 읽는 경로 */
  const table =
    picked.length > 0 ? (
      <>
        {/* 스크롤되는 상자는 키보드로도 스크롤할 수 있어야 한다 — 마우스 휠만으로
            읽히는 표는 키보드 사용자에게 잘린 표다 (axe scrollable-region-focusable) */}
        <div className="ctable" tabIndex={0} role="region" aria-label="KM 계단 값 표">
          <table className="data-table">
            <caption className="sr-only">강조한 곡선의 KM 계단 값</caption>
            <thead>
              <tr>
                <th scope="col">곡선</th>
                <th scope="col" className="ta-r">
                  경과
                </th>
                <th scope="col" className="ta-r">
                  생존확률
                </th>
                <th scope="col" className="ta-r">
                  잔존 (at-risk)
                </th>
                <th scope="col" className="ta-r">
                  복구
                </th>
                <th scope="col" className="ta-r">
                  절단
                </th>
              </tr>
            </thead>
            <tbody>
              {picked.flatMap((c) =>
                c.steps.slice(0, TABLE_STEPS).map((s) => (
                  <tr key={`${idOf(c)}-${s.t}`}>
                    <td>{nameOf(c)}</td>
                    <td className="num ta-r">{stepTime(s.t)}</td>
                    <td className="num ta-r">{pct(s.survival, 1)}</td>
                    <td className="num ta-r">{int(s.at_risk)}</td>
                    <td className="num ta-r">{s.events > 0 ? int(s.events) : DASH}</td>
                    <td className="num ta-r">{s.censored > 0 ? int(s.censored) : DASH}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
        <p className="km__tablenote">
          표에는 <strong>강조한 곡선</strong>의 계단만 있습니다 — 배경 회색 곡선은 문맥용이라 값을
          읽는 대상이 아닙니다. 곡선을 바꿔 고르면 그 값이 여기에 나옵니다.
          {picked.some((c) => c.steps.length > TABLE_STEPS) &&
            ` 계단이 ${TABLE_STEPS}개를 넘는 곡선은 앞 ${TABLE_STEPS}개만 실었습니다.`}
        </p>
      </>
    ) : undefined

  return (
    <ChartFrame
      title="Kaplan-Meier 생존곡선"
      question="고장이 난 뒤 아직 복구되지 않은 비율 — 복구를 못 본 세션은 그 시각까지의 정보로만 쓴다"
      meta={env?.meta}
      headline={
        curveA ? (
          <Headline
            value={curveA.median_reached ? humanMinutes(curveA.median_survival) : '미도달'}
            unit={curveA.median_reached ? `${nameOf(curveA)} 중위 생존` : '관측 기간 내 절반 미복구'}
            tone={curveA.median_reached ? 'neutral' : 'warn'}
          />
        ) : undefined
      }
      legend={[
        ...picked.map((c, i) => ({
          label: nameOf(c),
          color: i === 0 ? cssVar('--c-cat-1', '#0066cc') : cssVar('--c-cat-2', '#a35200'),
          pattern: 'line' as const,
        })),
        ...(background.length > 0
          ? [
              {
                label: `나머지 판정된 곡선 ${background.length}개 (배경)`,
                color: cssVar('--c-na', '#6f6f75'),
                pattern: 'line' as const,
              },
            ]
          : []),
      ]}
      controls={
        estimated.length > 0 ? (
          <div className="km__pick">
            <label className="km__picklabel" htmlFor="km-a">
              곡선 A
            </label>
            <select
              id="km-a"
              className="km__select"
              value={idA}
              onChange={(e) => setPickA(e.target.value)}
            >
              {estimated.map((c) => (
                <option key={idOf(c)} value={idOf(c)}>
                  {nameOf(c)} · 사건 {int(c.n_events)}건
                </option>
              ))}
            </select>
            <label className="km__picklabel" htmlFor="km-b">
              곡선 B
            </label>
            <select
              id="km-b"
              className="km__select"
              value={idB}
              onChange={(e) => setPickB(e.target.value)}
            >
              <option value={NONE}>없음</option>
              {estimated
                .filter((c) => idOf(c) !== idA)
                .map((c) => (
                  <option key={idOf(c)} value={idOf(c)}>
                    {nameOf(c)} · 사건 {int(c.n_events)}건
                  </option>
                ))}
            </select>
            <span className="km__pickhint">
              사건이 많은 곡선이 위에 있습니다 (결측 코호트 '미상'은 맨 뒤) — 사건 수가 곧 계단의
              해상도입니다
            </span>
          </div>
        ) : undefined
      }
      reading={`고장이 난 시각을 0 으로 두고, 시간이 지날수록 "아직 복구되지 않은" 비율이 어떻게 줄어드는지 그렸습니다. 곡선이 아래로 내려갈수록 빨리 복구된 것입니다. 복구를 관측하지 못한 세션(중도절단)은 버리지 않고 그 시각까지 "살아 있었다"는 정보로만 쓰이며, 곡선 위 + 표시가 그 시각입니다.`}
      encoding={[
        {
          role: '가로축',
          what: `경과 시간 (분 · 눈금 ${fmtTick(band.interval)})`,
          detail:
            '세션 시작을 0 으로 둔 경과 시간입니다. 원자료 단위는 분이지만 분 눈금으로는 이틀을 읽을 수 없어 시·일 경계에 맞는 간격만 후보로 두고 골랐습니다 — 500분 같은 눈금은 사람이 읽지 못합니다. 한 축의 단위는 하나로 고정합니다.',
        },
        {
          role: '세로축',
          what: '생존확률 (아직 복구되지 않은 비율)',
          detail:
            '0~100% 를 모두 그립니다. 확률은 절대 척도라 값이 몰려 있어도 축을 확대하지 않습니다 — 확대하면 "거의 다 복구됐다"와 "절반이 남았다"가 같은 그림이 됩니다.',
        },
        {
          role: '선 모양',
          what: '계단 (직선 보간 없음)',
          detail:
            'KM 추정값은 사건이 관측된 시각에만 바뀝니다. 두 사건 사이를 직선으로 이으면 관측하지 않은 시각의 값을 발명하는 것입니다 — 그 구간에서 우리가 아는 것은 "아직 아무도 복구되지 않았다" 뿐이고 그건 수평선입니다.',
        },
        {
          role: '색',
          what: '강조한 두 곡선 · 회색은 나머지',
          detail:
            '두 곡선은 좋고 나쁨이 아니라 서로 다른 그룹이므로 상태색(UP/DOWN/NA) 대신 비교 계열색을 씁니다. 42개를 각각 다른 색으로 그리면 색이 8색을 넘어 아무것도 구분되지 않으므로 나머지는 회색 배경으로 둡니다.',
        },
        {
          role: '곡선 위 +',
          what: '중도절단이 일어난 시각',
          detail:
            '그 시각에 복구를 못 본 채 관측을 잃었다는 뜻이며 사건이 아닙니다. + 가 몰린 구간 뒤의 곡선은 얇아진 표본에 기대므로, 아래 at-risk 표와 함께 읽어야 합니다.',
        },
        {
          role: '가로선',
          what: '생존 50% — 중위 생존 판독선',
          detail:
            '곡선이 이 선을 지나는 시각이 중위 생존입니다. 선에 닿지 않는 곡선은 중위 생존이 "없음"이 아니라 관측 기간 안에서 판정할 수 없다는 뜻입니다.',
        },
      ]}
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      onRetry={onRetry}
      table={table}
      notes={[
        {
          label: '이 곡선의 존재 이유가 중도절단이다.',
          text: 'STALE 로 잘린 세션을 종료로 세면 중위값이 STALE 임계값(1,440분) 그 자체에 붙습니다. 실측에서 그 버그로 MTTR 이 20배 틀렸고, 절단을 정식으로 처리한 뒤 71.9분이 되었습니다 (§3.3.1.1).',
        },
        withheld.length > 0
          ? {
              label: `곡선을 그리지 않은 그룹 ${withheld.length}개.`,
              text: `사건이 ${minEvents}건 미만이라 판정을 보류했습니다. 사건이 3건이면 계단 하나가 33%p 라 소수점을 붙여 제시할 정밀도가 아닙니다. 지우지 않고 아래 목록에 사유와 함께 남깁니다.`,
            }
          : '',
        {
          label: 'at-risk 가 얇아진 뒤의 곡선은 믿을 수 없다.',
          text: '곡선의 오른쪽 끝은 남은 몇 건이 좌우합니다. 곡선만 보면 그게 보이지 않으므로 구간별 잔존 수를 아래 표에 붙였습니다.',
        },
        thinned.length > 0
          ? {
              label: `서버가 계단을 추린 곡선 ${thinned.length}개.`,
              text: `표시 상한 때문에 계단 일부가 빠졌습니다 (예: ${nameOf(thinned[0] as KMCurve)} ${int(
                (thinned[0] as KMCurve).steps.length,
              )}/${int((thinned[0] as KMCurve).steps_total)}). 모양은 유지되지만 개별 사건 시각과 at-risk 는 다음 계단 기준으로 읽힙니다. 첫·마지막 계단과 중위 생존 교차점은 추리지 않습니다.`,
            }
          : '',
        {
          label: '결측 코호트를 버리지 않는다.',
          text: '그룹 값이 없는 세션은 "미상"이라는 하나의 그룹으로 남깁니다 (실측 제조사 결측 37.6%). 다른 그룹에 섞으면 그 그룹의 값이 오염되고, 버리면 가장 큰 코호트가 사라집니다.',
        },
      ]}
    >
      {notFitted ? (
        <div className="verdict-note" role="status">
          <p>
            <strong>서버가 곡선을 적합하지 않았습니다.</strong> 세션이 조회 상한에 걸렸거나 조건에
            맞는 다운타임 세션이 없습니다.
          </p>
          <p className="verdict-note__hint">
            잘린 표본으로 그린 KM 은 <em>조용히 틀린 곡선</em>입니다 — 특히 짧은 세션(= 복구를
            관측한 세션)이 빠지면 생존이 과대평가됩니다. 사유는 위 "서버가 붙인 주의"에 있습니다.
          </p>
        </div>
      ) : estimated.length === 0 && withheld.length > 0 ? (
        <div className="verdict-note" role="status">
          <p>
            <strong>이 축에서는 곡선을 그릴 수 없습니다.</strong> {withheld.length}개 그룹 전부가
            사건 {minEvents}건 미만이라 판정 보류입니다.
          </p>
          <p className="verdict-note__hint">
            "데이터가 없다"가 아니라 <em>"아직 판정할 수 없다"</em>입니다. 그룹별 사건 수와 사유는
            아래 목록에 있습니다.
          </p>
        </div>
      ) : (
        <>
          <EChart
            option={option}
            height={CHART_H}
            ariaLabel={`Kaplan-Meier 생존곡선. 강조 ${picked
              .map((c) => `${nameOf(c)} 중위 ${medianOf(c)}`)
              .join(', ')}. 배경 회색 ${background.length}개. 정확한 값은 표 보기에 있습니다.`}
          />

          <p className="km__count">
            그린 곡선 <span className="num">{estimated.length}</span>개 (강조{' '}
            <span className="num">{picked.length}</span> · 배경 회색{' '}
            <span className="num">{background.length}</span>) · 그리지 않은 곡선{' '}
            <span className="num">{withheld.length}</span>개 (판정 보류)
          </p>

          <ul className="km__medians">
            {picked.map((c, i) => (
              <li key={idOf(c)} className={`km__median km__median--${i}`}>
                <span className="km__medianname">{nameOf(c)}</span>
                <span className="km__medianvalue">
                  중위 생존 {medianOf(c)}
                </span>
                <span className="km__medianmeta">
                  대상 {int(c.n_subjects)} · 복구 {int(c.n_events)} · 절단 {int(c.n_censored)}
                </span>
              </li>
            ))}
          </ul>

          {/* 좁은 화면에서 표가 페이지를 밀지 않게 표 안에서만 가로 스크롤한다 */}
          <div
            className="km__atriskwrap"
            tabIndex={0}
            role="region"
            aria-label="구간별 at-risk 표"
          >
            <table className="km__atrisk data-table">
              <caption className="km__caption">
                구간별 at-risk — 그 시각까지 남아 있던 세션 수. <strong>얇아진 구간의 곡선은 몇
                건이 좌우합니다</strong> ({minEvents}건 미만은 강조).
              </caption>
              <thead>
                <tr>
                  <th scope="col">곡선</th>
                  {ticks.map((t) => (
                    <th key={t} scope="col" className="ta-r">
                      {fmtTick(t)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {picked.map((c) => (
                  <tr key={idOf(c)}>
                    <th scope="row">{nameOf(c)}</th>
                    {ticks.map((t) => {
                      const n = atRiskAt(c, t)
                      return (
                        <td
                          key={t}
                          className={`num ta-r${n < minEvents ? ' km__thin' : ''}`}
                          title={
                            n < minEvents
                              ? '표본이 얇아 이 뒤의 곡선은 신뢰할 수 없습니다'
                              : undefined
                          }
                        >
                          {int(n)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {withheld.length > 0 && (
            <details className="excl km__withheld">
              <summary>
                곡선을 그리지 않은 그룹 <span className="num">{withheld.length}</span>개 — 판정
                보류이며 사유를 함께 남깁니다
              </summary>
              <div className="ctable" tabIndex={0} role="region" aria-label="판정 보류 그룹 표">
                <table className="data-table">
                  <caption className="sr-only">판정 보류 그룹과 사유</caption>
                  <thead>
                    <tr>
                      <th scope="col">그룹</th>
                      <th scope="col" className="ta-r">
                        대상
                      </th>
                      <th scope="col" className="ta-r">
                        복구 관측
                      </th>
                      <th scope="col" className="ta-r">
                        중도절단
                      </th>
                      <th scope="col">사유</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withheld.map((c) => (
                      <tr key={idOf(c)}>
                        <td>{nameOf(c)}</td>
                        <td className="num ta-r">{int(c.n_subjects)}</td>
                        <td className="num ta-r">{int(c.n_events)}</td>
                        <td className="num ta-r">{int(c.n_censored)}</td>
                        <td className="excl__why">{c.reason ?? `사건 ${minEvents}건 미만`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </>
      )}
    </ChartFrame>
  )
}
